import axios from 'axios';
import auth from '@/api/auth';
import { setAuthToken, clearAuthToken } from '@/api/session';
import { APIService } from '@/http/APIService';
import { TOKEN_STORAGE_KEY, USERNAME_STORAGE_KEY } from '@/config/runtime';

let refreshProfileCancelSource = null;



import {
  LOGIN_BEGIN,
  LOGIN_FAILURE,
  LOGIN_SUCCESS,
  LOGOUT,
  REMOVE_TOKEN,
  SET_TOKEN,
} from './types';

// const isProduction = process.env.NODE_ENV === 'production';
const apiService = new APIService();

const initialState = {
  authenticating: false,
  error: false,
  token: null,
  username: null,
  verified: true,
  verified_sms_email: true,
  verified_email: false,
  verified_bank: true,
  staff: false,
  moderator: false,
  member_status: null,
  firstName: null,
  lastName: null,
  gender: null,
  user_id: null,
};

const getters = {
  isAuthenticated: state => !!state.token,
  username: state => state.username,
  isVerified: state => state.verified,
  isVerifiedSmsEmail: state => state.verified_sms_email,
  isVerifiedEmail: state => state.verified_email,
  isVerifiedBank: state => state.verified_bank,
  isStaff: state => state.staff,
  moderator: state => state.moderator,
  getMemberStatus: state => state.member_status,
  firstName: state => state.firstName,
  lastName: state => state.lastName,
  gender: state => state.gender,
  user_id: state => state.user_id,
  isLevel2(state) {
    return state.verified_sms_email;
  },
};

const actions = {
  login({ commit }, { username, password }) {
    commit(LOGIN_BEGIN);
    if (username.includes('@')) {
    return auth.login2(username, password)
      .then(({ data }) => {
        if (!data || !data.key) throw new Error('Server returned no auth token');
        commit(SET_TOKEN, data.key);
      })
      .then(() => {
        commit(LOGIN_SUCCESS);
        commit('username', username);
      })
      .then(() => apiService.getMe()
        .then((response) => {
          commit('verified', response.verified);
          commit('verified_sms_email', response.verified_sms_email);
          commit('verified_email', response.verified_email);
          commit('verified_bank', response.verified_bank);
          commit('staff', response.is_staff);
          commit('moderator', response.moderator);
          commit('setMemberStatus', response.member_status);
          commit('setFirstName', response.first_name);
          commit('setLastName', response.last_name);
          commit('setGender', response.gender);
          commit('user_id', response.id);
        })
        .catch((err) => {
          if (process.env.NODE_ENV !== 'production') {
            console.warn('[auth.login] post-login getMe failed', err?.response?.status);
          }
        }))
      .catch((error) => {
        commit(LOGIN_FAILURE);
        throw error;
      });

    } else {
      return auth.login(username, password)
        .then(({ data }) => {
          if (!data || !data.key) throw new Error('Server returned no auth token');
          commit(SET_TOKEN, data.key);
        })
        .then(() => {
          commit(LOGIN_SUCCESS);
          commit('username', username);
        })
        .then(() => apiService.getMe()
          .then((response) => {
            commit('verified', response.verified);
            commit('verified_sms_email', response.verified_sms_email);
            commit('verified_email', response.verified_email);
            commit('verified_bank', response.verified_bank);
            commit('staff', response.is_staff);
            commit('moderator', response.moderator);
            commit('setMemberStatus', response.member_status);
            commit('setFirstName', response.first_name);
            commit('setLastName', response.last_name);
            commit('setGender', response.gender);
            commit('user_id', response.id);
          })
          .catch((err) => {
            if (process.env.NODE_ENV !== 'production') {
              console.warn('[auth.login] post-login getMe failed', err?.response?.status);
            }
          }))
        .catch((error) => {
          commit(LOGIN_FAILURE);
          throw error;
        });
    }
  },
  logout({ commit }) {
    return auth.logout()
      .then(() => commit(LOGOUT))
      .finally(() => commit(REMOVE_TOKEN));
  },
  initialize({ commit }) {
    commit('resetTransientState');
    const token = localStorage.getItem(TOKEN_STORAGE_KEY);
    const username = localStorage.getItem(USERNAME_STORAGE_KEY);

    // if (isProduction && token) {
    //   commit(REMOVE_TOKEN);
    // }

    if (token) {
      commit(SET_TOKEN, token);
      commit('username', username)
    } else {
      commit(LOGOUT);
      commit(REMOVE_TOKEN);
    }
  },
  refreshProfile({ commit, state }) {
    if (!state.token) {
      return Promise.resolve(null);
    }

    if (refreshProfileCancelSource) {
      refreshProfileCancelSource.cancel('Operation canceled by a new request.');
    }
    refreshProfileCancelSource = axios.CancelToken.source();

    return apiService.getMe({ cancelToken: refreshProfileCancelSource.token })
      .then((response) => {
        commit('verified', response.verified);
        commit('verified_sms_email', response.verified_sms_email);
        commit('verified_email', response.verified_email);
        commit('verified_bank', response.verified_bank);
        commit('staff', response.is_staff);
        commit('moderator', response.moderator);
        commit('setMemberStatus', response.member_status);
        commit('setFirstName', response.first_name);
        commit('setLastName', response.last_name);
        commit('setGender', response.gender);
        commit('user_id', response.id);
        return response;
      })
      .catch((thrown) => {
        if (axios.isCancel(thrown)) {
          // console.log('Request canceled', thrown.message);
        } else {
          // handle error
        }
        return null;
      })
      .finally(() => {
        refreshProfileCancelSource = null;
      });
  },
};

const mutations = {
  resetTransientState(state) {
    state.authenticating = false;
    state.error = false;
  },
  user_id(state, user_id) {
    state.user_id = user_id;
  },
  verified(state, verified) {
    state.verified = verified;
  },
  verified_sms_email(state, verified) {
    state.verified_sms_email = verified;
  },

  verified_email(state, verified) {
    state.verified_email = verified;
  },

  verified_bank(state, verified) {
    state.verified_bank = verified;
  },

  staff(state, staff) {
    state.staff = staff;
  },
  moderator(state, moderator) {
    state.moderator = moderator;
  },

  setMemberStatus(state, member_status) {
    state.member_status = member_status;
  },
  username(state, username) {
    localStorage.setItem(USERNAME_STORAGE_KEY, username)
    state.username = username;
  },
  setFirstName(state, firstName) {
    state.firstName = firstName;
  },
  setLastName(state, lastName) {
    state.lastName = lastName;
  },
  setGender(state, gender) {
    state.gender = gender;
  },
  [LOGIN_BEGIN](state) {
    if (refreshProfileCancelSource) {
      refreshProfileCancelSource.cancel('Operation canceled by login begin.');
      refreshProfileCancelSource = null;
    }
    state.authenticating = false;
    state.error = false;
    state.token = null;
    state.username = null;
    state.verified = true;
    state.verified_sms_email = true;
    state.verified_email = false;
    state.verified_bank = true;
    state.staff = false;
    state.moderator = false;
    state.member_status = null;
    state.firstName = null;
    state.lastName = null;
    state.gender = null;
    state.user_id = null;
    // Drop any token before the login POST so /auth/login/ authenticates on
    // username/password alone — a stale token here yields 403 "Nesprávny token.".
    clearAuthToken();

    state.authenticating = true;
    state.error = false;
  },
  [LOGIN_FAILURE](state) {
    state.authenticating = false;
    state.error = true;
  },
  [LOGIN_SUCCESS](state) {
    state.authenticating = false;
    state.error = false;
  },
  [LOGOUT](state) {
    state.authenticating = false;
    state.error = false;
    state.token = null;
    state.username = null;
    state.verified = false;
    state.verified_bank = false;
    state.verified_sms_email = false;
    state.verified_email = false;
    state.staff = false;
    state.moderator = false;
    state.member_status = null;
    state.firstName = null;
    state.lastName = null;
    state.gender = null;
    state.user_id = null;

    state.token = null;
    state.authenticating = false;
    state.error = false;
  },
  [SET_TOKEN](state, token) {
    localStorage.setItem(TOKEN_STORAGE_KEY, token);
    setAuthToken(token);
    state.token = token;
  },
  [REMOVE_TOKEN](state) {
    localStorage.removeItem(TOKEN_STORAGE_KEY);
    localStorage.removeItem(USERNAME_STORAGE_KEY);
    clearAuthToken();
    state.token = null;
  },
};

export default {
  namespaced: true,
  state: initialState,
  getters,
  actions,
  mutations,
};
